import { useRef, useState } from "react";
import axios from "axios";
import { Upload, Download, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Generates and triggers download of a CSV template.
 * @param {string[]} columns
 * @param {string} filename
 */
function downloadTemplate(columns, filename) {
    const csv = columns.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

const TEMPLATES = {
    components: {
        columns: ["name", "sku", "description", "quantity", "unit_cost", "min_stock_level", "supplier_name", "supplier_contact", "category", "unit"],
        filename: "components_template.csv",
        endpoint: "/import/components",
        label: "Components",
    },
    "sub-assemblies": {
        columns: ["name", "sku", "description", "quantity", "min_stock_level"],
        filename: "sub_assemblies_template.csv",
        endpoint: "/import/sub-assemblies",
        label: "Sub-Assemblies",
    },
    assemblies: {
        columns: ["name", "sku", "description", "quantity", "min_stock_level"],
        filename: "assemblies_template.csv",
        endpoint: "/import/assemblies",
        label: "Assemblies",
    },
};

/**
 * @param {{ open: boolean, onOpenChange: (open: boolean) => void, type: 'components'|'sub-assemblies'|'assemblies', onSuccess: () => void }} props
 */
export default function BulkImportDialog({ open, onOpenChange, type, onSuccess }) {
    const fileInputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [mode, setMode] = useState("skip"); // "skip" | "overwrite" | "merge"

    const template = TEMPLATES[type];

    const handleClose = () => {
        setSelectedFile(null);
        setResult(null);
        setDragging(false);
        setMode("skip");
        onOpenChange(false);
    };

    const handleFile = (file) => {
        if (!file) return;
        const ext = file.name.split(".").pop().toLowerCase();
        if (!["csv", "xlsx", "xls"].includes(ext)) {
            toast.error("Unsupported file type. Please upload a .csv or .xlsx file.");
            return;
        }
        setSelectedFile(file);
        setResult(null);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        handleFile(e.dataTransfer.files[0]);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", selectedFile);
            formData.append("mode", mode);
            const res = await axios.post(`${API}${template.endpoint}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(res.data);
            const { created, updated, merged } = res.data;
            const total = (created || 0) + (updated || 0) + (merged || 0);
            if (total > 0) {
                const parts = [];
                if (created > 0) parts.push(`${created} created`);
                if (updated > 0) parts.push(`${updated} updated`);
                if (merged > 0) parts.push(`${merged} merged`);
                toast.success(`Import complete: ${parts.join(", ")}`);
                onSuccess();
            } else {
                toast.warning("No items were created or updated. Check the details below.");
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || "Import failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-lg rounded-none">
                <DialogHeader>
                    <DialogTitle className="uppercase text-xs tracking-wider font-mono">
                        Bulk Import — {template?.label}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Template download */}
                    <div className="flex items-center justify-between bg-muted/40 px-3 py-2 border border-border">
                        <div className="text-xs text-muted-foreground space-y-0.5">
                            <div>
                                <span className="font-semibold text-foreground">Required:</span>{" "}
                                <span className="font-mono">name, sku</span>
                                <span className="text-muted-foreground/60"> · or · </span>
                                <span className="font-mono">Nom, N° Article</span>
                            </div>
                            <div className="text-[11px] text-muted-foreground/70">
                                Accepts CSV (comma or semicolon) and XLSX · French headers auto-mapped
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs rounded-none h-7 gap-1 shrink-0"
                            onClick={() => downloadTemplate(template.columns, template.filename)}
                        >
                            <Download className="w-3 h-3" />
                            Template
                        </Button>
                    </div>

                    {/* Duplicate mode selector */}
                    <div className="space-y-1.5">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">On duplicate SKU</p>
                        <div className="grid grid-cols-3 gap-1">
                            {[
                                { value: "skip", label: "Skip" },
                                { value: "overwrite", label: "Overwrite" },
                                { value: "merge", label: "Merge" },
                            ].map(opt => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => setMode(opt.value)}
                                    className={`text-xs py-1.5 border uppercase tracking-wider transition-colors ${mode === opt.value
                                        ? "bg-foreground text-background border-foreground"
                                        : "border-border text-muted-foreground hover:border-foreground/40"
                                        }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <p className="text-[11px] text-muted-foreground/70">
                            {mode === "skip" && "Rows with existing SKUs will be ignored."}
                            {mode === "overwrite" && "Existing records are fully replaced with import data."}
                            {mode === "merge" && "Quantities are added. Blank fields in existing records are filled from the file."}
                        </p>
                    </div>

                    {/* Drop zone */}
                    {!result && (
                        <div
                            className={`border-2 border-dashed rounded-none flex flex-col items-center justify-center gap-2 p-8 cursor-pointer transition-colors ${dragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                                }`}
                            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <FileSpreadsheet className="w-8 h-8 text-muted-foreground" />
                            {selectedFile ? (
                                <div className="text-center">
                                    <p className="text-sm font-medium">{selectedFile.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {(selectedFile.size / 1024).toFixed(1)} KB — click to change
                                    </p>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <p className="text-sm font-medium">Drop your file here</p>
                                    <p className="text-xs text-muted-foreground">CSV or XLSX · max 10 MB</p>
                                </div>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                className="hidden"
                                onChange={(e) => handleFile(e.target.files[0])}
                            />
                        </div>
                    )}

                    {/* Results */}
                    {result && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-5 gap-2">
                                <div className="flex flex-col items-center border border-border p-3 gap-1">
                                    <span className="text-lg font-mono font-bold">{result.total}</span>
                                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Total</span>
                                </div>
                                <div className="flex flex-col items-center border border-green-600/40 bg-green-600/5 p-3 gap-1">
                                    <span className="text-lg font-mono font-bold text-green-600">{result.created}</span>
                                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Created</span>
                                </div>
                                <div className="flex flex-col items-center border border-blue-500/40 bg-blue-500/5 p-3 gap-1">
                                    <span className="text-lg font-mono font-bold text-blue-500">{result.updated ?? 0}</span>
                                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Updated</span>
                                </div>
                                <div className="flex flex-col items-center border border-violet-500/40 bg-violet-500/5 p-3 gap-1">
                                    <span className="text-lg font-mono font-bold text-violet-500">{result.merged ?? 0}</span>
                                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Merged</span>
                                </div>
                                <div className="flex flex-col items-center border border-orange-500/40 bg-orange-500/5 p-3 gap-1">
                                    <span className="text-lg font-mono font-bold text-orange-500">{result.skipped}</span>
                                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Skipped</span>
                                </div>
                            </div>

                            {result.errors.length > 0 && (
                                <div>
                                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1 font-mono">
                                        Errors / Warnings
                                    </p>
                                    <ScrollArea className="h-36 border border-border">
                                        <div className="p-2 space-y-1">
                                            {result.errors.map((err, i) => (
                                                <div key={i} className="flex items-start gap-2 text-xs">
                                                    <AlertTriangle className="w-3 h-3 mt-0.5 text-orange-500 shrink-0" />
                                                    <span className="font-mono text-muted-foreground">Row {err.row}</span>
                                                    {err.sku && <Badge variant="outline" className="text-[10px] px-1 py-0 rounded-none">{err.sku}</Badge>}
                                                    <span>{err.error}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                </div>
                            )}

                            <Button
                                variant="outline"
                                size="sm"
                                className="w-full rounded-none text-xs uppercase tracking-wider"
                                onClick={() => { setResult(null); setSelectedFile(null); }}
                            >
                                Import Another File
                            </Button>
                        </div>
                    )}
                </div>

                {!result && (
                    <DialogFooter className="gap-2">
                        <Button variant="outline" className="rounded-none text-xs uppercase tracking-wider" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            className="rounded-none text-xs uppercase tracking-wider gap-2"
                            onClick={handleUpload}
                            disabled={!selectedFile || uploading}
                        >
                            <Upload className="w-3 h-3" />
                            {uploading ? "Importing…" : "Import"}
                        </Button>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog >
    );
}
